import { PROTOCOL_TEST_IDS } from '@/components/admin/protocoles/contracts';
import { DirectionField } from '@/components/admin/protocoles/step/direction-field';
import { DuplicateButton } from '@/components/ui/duplicate-button';
import { Tooltip } from '@/components/ui/tooltip';
import { conditionDuplicationBlocked, duplicateCondition } from '@/lib/duplication';
import {
  ACTION_KINDS,
  ACTION_LABEL,
  COUNTER_TRIGGER_LABEL,
  OBJECTIVE_CLASS_LABEL,
  objectiveTitle,
  stepTitle,
} from '@/lib/protocol-labels';
import type { ActionContext } from '@/lib/protocol-step-draft';
import {
  addAction,
  addGroup,
  addTrigger,
  allObjectives,
  availableTriggerObjectives,
  moveCondition,
  newCondition,
  otherSteps,
  removeAction,
  removeCondition,
  removeTrigger,
  replaceCondition,
  setActionKind,
  setActionParam,
  setTrigger,
  setTriggerOutcome,
  triggerValue,
} from '@/lib/protocol-step-draft';
import type {
  CriterionCard,
  ExitAction,
  ExitActionKind,
  ExitCondition,
  ProtocolStepDraft,
} from '@matter_extractor/shared';
import {
  PROTOCOL_CLOSE_TEXT_MAX,
  PROTOCOL_CONDITIONS_MAX,
  PROTOCOL_DEFAULT_CLOSE_TEXT,
  isTerminalAction,
  objectiveOutcomeSchema,
} from '@matter_extractor/shared';
import { GripVertical, Plus, X } from 'lucide-react';
import type { DragEvent as ReactDragEvent } from 'react';
import { useState } from 'react';

/**
 * The exit conditions of a step: an ORDERED list, evaluated after the
 * garde-fous, the first true one winning (refonte modulaire §8.4; maquette
 * `conditionHTML`).
 *
 * They deviate the implicit logic, they do not replace it - and a condition
 * that fires puts the implicit logic to sleep for that turn even when none of
 * its actions is terminal. Which is why the card is drawn as a sentence and not
 * as a form: « quand A et B ou C → invalider X puis aller à 3 · Contexte » is
 * how §8.4 words it, and the selects sit inside that sentence.
 *
 * ## The order is data, so it is draggable and numbered
 *
 * « la première vraie gagne » makes the rank of a card its priority. The number
 * is therefore on the card and not merely implied by its position, and the
 * gesture that changes it is a drag, like the frise's.
 *
 * ## « et » groups, « ou » between them, and no parentheses
 *
 * The form of the filters everybody knows (§8.4). A group is a conjunction, the
 * groups are a disjunction, and the nesting stops there: an editor that allowed
 * parentheses would need a reading order nobody can see on screen.
 *
 * ## An action's kind REPLACES the action
 *
 * The seven kinds are a discriminated union and three of them carry a
 * parameter. `setActionKind` mints a fresh action with a target that exists -
 * the first other step, the first objective of the protocol - because a select
 * that opened on nothing would write a dangling reference the moment it was
 * chosen. `normalizeActions` runs after every change, so the terminal one is
 * always last and always alone.
 */

export interface ExitConditionsProps {
  step: ProtocolStepDraft;
  /** The whole draft: a trigger reaches back, a jump reaches anywhere. */
  steps: readonly ProtocolStepDraft[];
  criteria: readonly CriterionCard[];
  readOnly: boolean;
  onChangeConditions: (next: readonly ExitCondition[]) => void;
}

/** The direction of an action - one field per action, so the mark is local. */
const ACTION_DIRECTION_TESTID = 'protocole-action-direction';

const SELECT_BOX =
  'h-8 max-w-full rounded-md border-2 border-gray-300 bg-white px-2 text-caption text-gray-800 focus-visible:border-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:bg-gray-50';

const KEYWORD = 'font-bold text-gray-700 text-xs uppercase tracking-wide';

function toActionKind(value: string): ExitActionKind | null {
  return ACTION_KINDS.find((kind) => kind === value) ?? null;
}

export function ExitConditions({
  step,
  steps,
  criteria,
  readOnly,
  onChangeConditions,
}: ExitConditionsProps) {
  const [drag, setDrag] = useState<{ id: string; overId: string | null } | null>(null);

  const conditions = step.conditions;
  const duplicationBlocked = conditionDuplicationBlocked(conditions);
  const others = otherSteps(steps, step.id);
  const reachable = availableTriggerObjectives(steps, step.id);
  const everyObjective = allObjectives(steps);
  const here = steps.findIndex((one) => one.id === step.id);
  const context: ActionContext = {
    otherStepIds: others.map((one) => one.id),
    objectiveIds: everyObjective.map((place) => place.objective.id),
  };

  const change = (next: ExitCondition) => onChangeConditions(replaceCondition(conditions, next));

  return (
    <div>
      <div className="flex flex-col gap-3">
        {conditions.map((condition, index) => (
          <div
            key={condition.id}
            data-testid={PROTOCOL_TEST_IDS.condition}
            data-condition-id={condition.id}
            draggable={!readOnly}
            onDragStart={() => setDrag({ id: condition.id, overId: null })}
            onDragOver={(event: ReactDragEvent<HTMLElement>) => {
              if (drag === null) return;
              event.preventDefault();
              setDrag((was) => (was === null ? null : { ...was, overId: condition.id }));
            }}
            onDrop={(event: ReactDragEvent<HTMLElement>) => {
              event.preventDefault();
              if (drag === null) return;
              const from = conditions.findIndex((one) => one.id === drag.id);
              const to = index;
              setDrag(null);
              if (from < 0 || from === to) return;
              onChangeConditions(moveCondition(conditions, from, to));
            }}
            onDragEnd={() => setDrag(null)}
            className={`relative rounded-md border-2 border-gray-200 bg-white p-4 pr-12 ${
              drag?.id === condition.id ? 'opacity-40' : ''
            }`}
          >
            <div className="flex gap-3">
              <span className="grid size-6 shrink-0 place-items-center rounded-circle bg-bleu-nuit-tint font-bold text-bleu-nuit text-xs">
                {index + 1}
              </span>
              <div className="min-w-0 flex-1 space-y-2">
                <p className="flex flex-wrap items-center gap-2 text-sm">
                  <span className={KEYWORD}>quand</span>
                  <span className="text-gray-600 text-xs">
                    {condition.groups.length > 1
                      ? 'l’un de ces groupes est vrai · dans un groupe, tout doit être vrai'
                      : 'tout doit être vrai'}
                  </span>
                </p>

                {/* The key is the condition's id plus a RANK, for the groups,
                    the triggers and the actions alike: none of the three
                    carries an id of its own - each one IS its rank inside the
                    condition - and keying on the content would remount the
                    select an operator is typing in. */}
                {condition.groups.map((group, groupIndex) => (
                  <div key={`${condition.id}-g${groupIndex}`}>
                    {groupIndex === 0 ? null : (
                      <p className="my-1.5 flex items-center gap-2.5 font-bold text-gray-500 text-xs">
                        <span aria-hidden="true" className="h-px flex-1 bg-rail" />
                        ou
                        <span aria-hidden="true" className="h-px flex-1 bg-rail" />
                      </p>
                    )}
                    <div className="flex flex-col gap-2 border-gray-300 border-l-2 py-1 pl-3">
                      {group.map((trigger, triggerIndex) => (
                        <div
                          key={`${condition.id}-g${groupIndex}-t${triggerIndex}`}
                          className="flex flex-wrap items-center gap-2"
                        >
                          {triggerIndex === 0 ? null : <span className={KEYWORD}>et</span>}
                          <select
                            data-testid={PROTOCOL_TEST_IDS.trigger}
                            aria-label="Déclencheur"
                            value={triggerValue(trigger)}
                            disabled={readOnly}
                            onChange={(event) =>
                              change(
                                setTrigger(
                                  condition,
                                  groupIndex,
                                  triggerIndex,
                                  event.currentTarget.value,
                                ),
                              )
                            }
                            className={SELECT_BOX}
                          >
                            <optgroup label="Un objectif">
                              {reachable.map((place) => (
                                <option
                                  key={place.objective.id}
                                  value={`obj:${place.objective.id}`}
                                >
                                  {objectiveTitle(place.objective.name, 0)}
                                  {place.stepIndex === here
                                    ? ''
                                    : ` · étape ${place.stepIndex + 1}`}
                                </option>
                              ))}
                            </optgroup>
                            <optgroup label="Un type d’objectif">
                              {Object.entries(OBJECTIVE_CLASS_LABEL).map(([which, label]) => (
                                <option key={which} value={`type:${which}`}>
                                  {label}
                                </option>
                              ))}
                            </optgroup>
                            <optgroup label="Un compteur">
                              {Object.entries(COUNTER_TRIGGER_LABEL).map(([kind, label]) => (
                                <option key={kind} value={kind}>
                                  {label}
                                </option>
                              ))}
                            </optgroup>
                          </select>
                          {trigger.kind === 'objective-status' ? (
                            <select
                              data-testid={PROTOCOL_TEST_IDS.triggerOutcome}
                              aria-label="Statut"
                              value={trigger.to}
                              disabled={readOnly}
                              onChange={(event) => {
                                const to = objectiveOutcomeSchema.safeParse(
                                  event.currentTarget.value,
                                );
                                if (!to.success) return;
                                change(
                                  setTriggerOutcome(condition, groupIndex, triggerIndex, to.data),
                                );
                              }}
                              className={SELECT_BOX}
                            >
                              <option value="succeeded">devient réussi</option>
                              <option value="failed">devient raté</option>
                            </select>
                          ) : null}
                          {readOnly ? null : (
                            <IconButton
                              label="Retirer ce déclencheur"
                              onClick={() =>
                                change(removeTrigger(condition, groupIndex, triggerIndex))
                              }
                            />
                          )}
                        </div>
                      ))}
                      {readOnly ? null : (
                        <AddLine
                          testId={PROTOCOL_TEST_IDS.addTrigger}
                          label="et"
                          onClick={() => change(addTrigger(condition, groupIndex))}
                        />
                      )}
                    </div>
                  </div>
                ))}

                {readOnly ? null : (
                  <AddLine
                    testId={PROTOCOL_TEST_IDS.addGroup}
                    label="ou · nouveau groupe"
                    onClick={() => change(addGroup(condition))}
                  />
                )}

                <div className="flex flex-col gap-2 pt-1">
                  {condition.actions.map((action, actionIndex) => (
                    <div
                      key={`${condition.id}-a${actionIndex}`}
                      className="flex flex-wrap items-center gap-2 text-sm"
                    >
                      <span className={actionIndex === 0 ? KEYWORD : `${KEYWORD} text-gray-500`}>
                        {actionIndex === 0 ? '→' : 'puis'}
                      </span>
                      <select
                        data-testid={PROTOCOL_TEST_IDS.action}
                        aria-label="Action"
                        value={action.kind}
                        disabled={readOnly}
                        onChange={(event) => {
                          const kind = toActionKind(event.currentTarget.value);
                          if (kind === null) return;
                          change(setActionKind(condition, actionIndex, kind, context));
                        }}
                        className={SELECT_BOX}
                      >
                        {ACTION_KINDS.map((kind) => (
                          <option key={kind} value={kind}>
                            {ACTION_LABEL[kind]}
                          </option>
                        ))}
                      </select>
                      <ActionParams
                        action={action}
                        condition={condition}
                        actionIndex={actionIndex}
                        steps={steps}
                        stepId={step.id}
                        criteria={criteria}
                        readOnly={readOnly}
                        onChange={change}
                      />
                      {isTerminalAction(action) && condition.actions.length > 1 ? (
                        <span className="text-gray-600 text-xs">
                          action terminale, toujours en dernier
                        </span>
                      ) : null}
                      {readOnly ? null : (
                        <IconButton
                          label="Retirer l’action"
                          onClick={() => change(removeAction(condition, actionIndex))}
                        />
                      )}
                    </div>
                  ))}
                  {readOnly ? null : (
                    <AddLine
                      testId={PROTOCOL_TEST_IDS.addAction}
                      label={
                        condition.actions.some(isTerminalAction)
                          ? 'action non terminale, avant la dernière'
                          : 'action'
                      }
                      onClick={() => change(addAction(condition, context))}
                    />
                  )}
                </div>
              </div>
            </div>

            {readOnly ? null : (
              <div className="absolute top-3 right-3 grid justify-items-center gap-1">
                <Tooltip label="Glisser pour changer la priorité">
                  <span className="grid cursor-grab p-1">
                    <GripVertical aria-hidden="true" className="size-4 text-gray-400" />
                  </span>
                </Tooltip>
                {/* The copy icon, just before the cross (§4.2). */}
                <DuplicateButton
                  testId={PROTOCOL_TEST_IDS.duplicateCondition}
                  label={`Dupliquer la condition de sortie ${index + 1}`}
                  blocked={duplicationBlocked}
                  shape="compact"
                  onClick={() => {
                    const done = duplicateCondition(conditions, condition.id);
                    if (done !== null) onChangeConditions(done.list);
                  }}
                />
                <IconButton
                  testId={PROTOCOL_TEST_IDS.removeCondition}
                  label="Supprimer la condition de sortie"
                  onClick={() => onChangeConditions(removeCondition(conditions, condition.id))}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3.5 text-caption text-gray-600">
        {readOnly ? null : (
          <button
            type="button"
            data-testid={PROTOCOL_TEST_IDS.addCondition}
            disabled={conditions.length >= PROTOCOL_CONDITIONS_MAX}
            onClick={() => onChangeConditions([...conditions, newCondition()])}
            className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-md px-3.5 font-semibold text-bleu-nuit text-sm hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:text-gray-500 disabled:hover:bg-transparent"
          >
            <Plus aria-hidden="true" className="size-4" strokeWidth={2.5} />
            Ajouter une condition de sortie
          </button>
        )}
        <span>sans condition, la logique implicite s’applique seule</span>
      </div>
    </div>
  );
}

/** The one parameter an action carries, drawn beside its kind (§8.4). */
function ActionParams({
  action,
  condition,
  actionIndex,
  steps,
  stepId,
  criteria,
  readOnly,
  onChange,
}: {
  action: ExitAction;
  condition: ExitCondition;
  actionIndex: number;
  steps: readonly ProtocolStepDraft[];
  stepId: string;
  criteria: readonly CriterionCard[];
  readOnly: boolean;
  onChange: (next: ExitCondition) => void;
}) {
  if (action.kind === 'direction') {
    return (
      <span className="min-w-60 flex-1">
        <DirectionField
          value={action.text}
          onChange={(text) =>
            onChange(setActionParam(condition, actionIndex, { field: 'text', value: text }))
          }
          readOnly={readOnly}
          placeholder="La consigne servie à l’interviewer au tour suivant."
          ariaLabel="Direction d’IA"
          testId={ACTION_DIRECTION_TESTID}
          insertTestId={null}
          steps={steps}
          stepId={stepId}
          criteria={criteria}
          minHeightClass="min-h-16"
        />
      </span>
    );
  }

  if (action.kind === 'go-to-step') {
    const others = otherSteps(steps, stepId);
    return (
      <select
        aria-label="Étape visée"
        value={action.stepId}
        disabled={readOnly}
        onChange={(event) =>
          onChange(
            setActionParam(condition, actionIndex, {
              field: 'stepId',
              value: event.currentTarget.value,
            }),
          )
        }
        className={SELECT_BOX}
      >
        {others.map((one) => (
          <option key={one.id} value={one.id}>
            {stepTitle({ index: steps.indexOf(one), name: one.name })}
          </option>
        ))}
      </select>
    );
  }

  if (action.kind === 'invalidate-objective') {
    return (
      <select
        aria-label="Objectif à invalider"
        value={action.objectiveId}
        disabled={readOnly}
        onChange={(event) =>
          onChange(
            setActionParam(condition, actionIndex, {
              field: 'objectiveId',
              value: event.currentTarget.value,
            }),
          )
        }
        className={SELECT_BOX}
      >
        {allObjectives(steps).map((place) => (
          <option key={place.objective.id} value={place.objective.id}>
            {objectiveTitle(place.objective.name, 0)} · étape {place.stepIndex + 1}
          </option>
        ))}
      </select>
    );
  }

  if (action.kind === 'close-experience') {
    const isDefault = action.text === PROTOCOL_DEFAULT_CLOSE_TEXT;
    return (
      <>
        <input
          aria-label="Texte de clôture"
          value={action.text}
          readOnly={readOnly}
          maxLength={PROTOCOL_CLOSE_TEXT_MAX}
          onChange={(event) =>
            onChange(
              setActionParam(condition, actionIndex, {
                field: 'text',
                value: event.currentTarget.value,
              }),
            )
          }
          className="h-8 min-w-60 flex-1 rounded-md border-2 border-gray-300 bg-white px-2.5 text-caption read-only:bg-gray-50 read-only:text-gray-700 focus-visible:border-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        {isDefault || readOnly ? (
          <span className="text-gray-600 text-xs">texte par défaut</span>
        ) : (
          <button
            type="button"
            onClick={() =>
              onChange(
                setActionParam(condition, actionIndex, {
                  field: 'text',
                  value: PROTOCOL_DEFAULT_CLOSE_TEXT,
                }),
              )
            }
            className="cursor-pointer text-bleu-nuit text-xs underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            remettre le texte par défaut
          </button>
        )}
      </>
    );
  }

  return null;
}

/** « + et », « + ou · nouveau groupe », « + action » - one shape for the three. */
function AddLine({
  testId,
  label,
  onClick,
}: {
  testId: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <div>
      <button
        type="button"
        data-testid={testId}
        onClick={onClick}
        className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md px-2.5 font-semibold text-bleu-nuit text-caption hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Plus aria-hidden="true" className="size-3.5" strokeWidth={2.5} />
        {label}
      </button>
    </div>
  );
}

/** A cross in a gutter: the one remove gesture of every line of a condition. */
function IconButton({
  testId,
  label,
  onClick,
}: {
  testId?: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      {...(testId === undefined ? {} : { 'data-testid': testId })}
      aria-label={label}
      onClick={onClick}
      className="inline-flex size-7 cursor-pointer items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-rouge-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <X aria-hidden="true" className="size-3.5" />
    </button>
  );
}
